import { useState } from "react";
import axios from "axios";
import { CalendarCheck, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { BookingLayout, BOOK_API, SlotPicker, fmtSlot } from "../components/site/Booking";
import { Field } from "../components/site/Field";
import { AddressBlock } from "../components/site/QuoteAddress";
import { PhotoPicker } from "../components/site/PhotoPicker";
import { addressErrors } from "../lib/npa";

const ITEMS = [["salon", "Salon"], ["cuisine", "Cuisine"], ["dressing", "Dressing"], ["cave/garage", "Cave / garage"], ["bureau", "Bureau"], ["déménagement", "Déménagement"], ["autre", "Autre"]];
const EMPTY = { name: "", phone: "", email: "", street: "", npa: "", city: "", access: "", message: "", website: "" };

const validate = (v) => {
  const e = { ...addressErrors(v) };
  if (v.name.trim().length < 2) e.name = "Veuillez indiquer votre nom.";
  if (v.phone.replace(/\D/g, "").length < 9) e.phone = "Numéro de téléphone invalide.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) e.email = "Adresse email invalide.";
  return e;
};

export default function ReserverVisite() {
  const [slot, setSlot] = useState(null);
  const [v, setV] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(null);
  const setValue = (k, val) => { setV((x) => ({ ...x, [k]: val })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const on = (k) => (ev) => setValue(k, ev.target.value);
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v);
    setErrors(e);
    if (Object.keys(e).length) return document.getElementById(Object.keys(e)[0])?.focus();
    setState("loading");
    setMsg("");
    try {
      const { data } = await axios.post(BOOK_API, {
        date: slot.date, time: slot.time, name: v.name.trim(), phone: v.phone.trim(), email: v.email.trim(), items, message: v.message.trim(), website: v.website,
        adresse: { street: v.street.trim(), npa: v.npa.trim(), city: v.city.trim(), notes: v.access.trim() },
        photo_ids: photos.filter((p) => p.status === "done").map((p) => p.id),
      });
      setDone(data);
      setState("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (er) {
      setState("idle");
      if (er.response?.status === 409) setSlot(null);
      setMsg(er.response?.data?.detail && typeof er.response.data.detail === "string" ? er.response.data.detail : "La réservation n'a pas pu être enregistrée. Vérifiez vos informations ou appelez-nous.");
    }
  };
  if (state === "done") {
    return (
      <BookingLayout title="Visite réservée" eyebrow="Rangement à domicile" testId="booking-done-page">
        <div className="rounded-2xl bg-white p-7 text-center" data-testid="booking-success">
          <CalendarCheck className="mx-auto h-12 w-12 text-cs-yellow-dark" />
          <h2 className="mt-4 font-display text-2xl font-extrabold text-cs-navy">Merci, à bientôt !</h2>
          <p className="mt-2 text-cs-ink/80">Votre visite gratuite est confirmée le <b>{done?.when}</b>. Vous recevez un email de confirmation avec l'invitation calendrier et un lien pour modifier ou annuler.</p>
          <Link to="/" className="btn-accent mt-6 inline-flex px-6 py-3">Retour à l'accueil</Link>
        </div>
      </BookingLayout>
    );
  }
  return (
    <BookingLayout title="Réserver une visite gratuite" eyebrow="Rangement à domicile" testId="booking-page">
      <section className="rounded-2xl bg-white p-5 sm:p-7">
        <h2 className="font-display text-xl font-extrabold text-cs-navy">1. Choisissez un créneau</h2>
        <div className="mt-4"><SlotPicker value={slot} onPick={setSlot} /></div>
        <p className="mt-4 text-sm text-cs-ink/70">Vous préférez qu'on vous appelle ? <Link to="/#devis" className="font-bold text-cs-navy underline" data-testid="booking-callback-link">Je préfère être rappelé</Link></p>
      </section>
      {slot && (
        <form onSubmit={submit} noValidate className="mt-6 grid grid-cols-1 gap-x-5 gap-y-4 rounded-2xl bg-white p-5 sm:grid-cols-2 sm:p-7" data-testid="booking-form">
          <h2 className="font-display text-xl font-extrabold text-cs-navy sm:col-span-2">2. Vos coordonnées</h2>
          <p className="rounded-lg bg-cs-yellow/20 px-4 py-2.5 text-sm font-semibold text-cs-navy sm:col-span-2" data-testid="booking-selected">Créneau choisi : {fmtSlot(slot)}</p>
          <Field id="name" label="Nom et prénom *" value={v.name} onChange={on("name")} error={errors.name} autoComplete="name" className="sm:col-span-2" />
          <Field id="phone" label="Téléphone *" type="tel" value={v.phone} onChange={on("phone")} error={errors.phone} autoComplete="tel" />
          <Field id="email" label="Email *" type="email" value={v.email} onChange={on("email")} error={errors.email} autoComplete="email" />
          <div className="hr-sep sm:col-span-2" />
          <h3 className="font-display text-base font-bold text-cs-navy sm:col-span-2">Adresse de la visite</h3>
          <AddressBlock values={v} setValue={setValue} errors={errors} group="" />
          <Field id="access" label="Étage / accès / remarques" value={v.access} onChange={on("access")} className="sm:col-span-2" maxLength={300} />
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-sm font-semibold text-cs-navy">Que souhaitez-vous ranger ?</legend>
            <div className="flex flex-wrap gap-2" data-testid="booking-items">
              {ITEMS.map(([k, l]) => (
                <label key={k} className={`cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold ${items.includes(k) ? "border-cs-navy bg-cs-navy text-white" : "border-cs-navy/20 text-cs-navy"}`}>
                  <input type="checkbox" className="sr-only" checked={items.includes(k)} onChange={(e) => setItems(e.target.checked ? [...items, k] : items.filter((x) => x !== k))} data-testid={`booking-item-${k.replace("/", "-")}`} />{l}
                </label>
              ))}
            </div>
          </fieldset>
          <Field id="message" label="Quelques mots (facultatif)" as="textarea" rows={3} value={v.message} onChange={on("message")} className="sm:col-span-2" />
          <div className="sm:col-span-2"><PhotoPicker photos={photos} setPhotos={setPhotos} website={v.website} /></div>
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={on("website")} className="absolute left-[-9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="booking-honeypot" />
          {msg && <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 sm:col-span-2" data-testid="booking-error">{msg}</p>}
          <button type="submit" disabled={state === "loading" || photos.some((p) => p.status === "uploading")} className="btn-accent w-full py-4 text-base sm:col-span-2" data-testid="booking-submit">
            {state === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirmer la visite gratuite"}
          </button>
          <p className="text-xs text-cs-ink/60 sm:col-span-2">Vos données servent uniquement à organiser la visite. <Link to="/confidentialite" className="underline">Confidentialité</Link></p>
        </form>
      )}
    </BookingLayout>
  );
}
